import { NgZone, Injectable } from '@angular/core';
import { Subject, timer ,  Observable, Subscription } from 'rxjs';
import { HttpHeaders } from '@angular/common/http';
import { DeviceCheckinCompleted, DeviceCheckinResultCode } from './models/device-checkin-completed';
import { DeviceCheckinFailure } from './models/device-checkin-failure';
import { CheckinStateBroadcastService, DeviceCheckinStatusMessage, DeviceStatusRequest, DeviceIdToCheckinStateMap } from './broadcasters/checkin.broadcaster';
import { GlobalEvents } from '../../core/global-event-constants';
import { UntypedTopicUpdateModel } from './models/topic-update';
import { TopicDescriptorModel } from './models/topic-descriptor';
import { AuthenticationService, DeviceService, FeatureToggleService, GlobalEventBusService, IRequestOptions, LicenceInfoService, XDeviceService } from '@nx-fe/services';
import * as signalR from '@microsoft/signalr';
import { HttpService } from '../../../../../../libs/services/http.service';

interface DeviceCheckinConfig {
    MaximumCheckInTimeout: number;
}

@Injectable({
    providedIn: 'root'
})
export class SignalRCoreService {
    private _subscribeTopicSubscription: Subscription;
    private _unsubscribeTopicSubscription: Subscription;
    private readonly onDeviceCheckinStatusChanged: CheckinStateBroadcastService;
    private _configuration: DeviceCheckinConfig = { MaximumCheckInTimeout:  30000 };
    connectionInitiated: boolean;

    constructor(
        private http: HttpService,
        private authenticationService: AuthenticationService,
        private deviceService: XDeviceService,
        private saLicenceInfoService: LicenceInfoService,
        private globalEventBus: GlobalEventBusService,
        private ftService: FeatureToggleService
    ) {
        const isSignalRCoreEnabled = ftService.isEnabled('EnableAspNetCore') && ftService.isEnabled('EnableAspNetCore_SignalRService');
        if(isSignalRCoreEnabled === false) {
            console.log('SignalRCore disabled. Skipping SignalRCore initialization');
            return;
        }

        this.onDeviceCheckinStatusChanged = new CheckinStateBroadcastService();

        this._subscribeTopicSubscription = this.globalEventBus.register(GlobalEvents.subscribeTopic, (result: TopicDescriptorModel) => {
            this.connection.invoke('SubscribeTopic', result);
        });

        this._unsubscribeTopicSubscription = this.globalEventBus.register(GlobalEvents.unsubscribeTopic, (result: TopicDescriptorModel) => {
            this.connection.invoke('UnsubscribeTopic', result);
        });

        this.tryInitiateConnect();
    }

    private checkinStatus: DeviceIdToCheckinStateMap = {};
    private connection: signalR.HubConnection;

    tryInitiateConnect = () => {
        this.saLicenceInfoService.getLicenceInfoPromise()?.then(() => {
            if (!this.connectionInitiated) {
                this.tryInitiateConnectInternal();
            }
        });
    };

    /** This method requires licenseDetails to be available.
     * Should be called only from tryInitiateConnect()
     */
    tryInitiateConnectInternal = () => {
        if (!this.isLicenseActive()) {
            console.warn('SignalR service is disabled due to the inactive license');
            return;
        }

        this.connectionInitiated = true;

        this.onDeviceCheckinStatusChanged.onMessage = (msg: DeviceCheckinStatusMessage) => {
            switch (msg.request) {
                case DeviceStatusRequest.WatchDevice:
                this.startCheckinInProgress(msg.deviceId, this._configuration.MaximumCheckInTimeout);
                    break;
                case DeviceStatusRequest.StopWatchingDevice:
                    this.stopCheckinInProgress(msg.deviceId);
                    break;
                case DeviceStatusRequest.Refresh:
                    break;
            }
        };

        // If there's no feature toggle, Service does nothing
        this.setupHub().then((conn) => {
            this.connection = conn;

            this.ConfigureMessaging();
            this.startConnection();
        });
    };

    private isLicenseActive = (): boolean => {
        const result = this.saLicenceInfoService && this.saLicenceInfoService.isLicenseActive();
        return result;
    };

    private startCheckinInProgress = (deviceId: string, timeout: number) => {
        this.checkinStatus[deviceId] = new Date();
        this.deviceService.requestDevicesCheckin(deviceId, true, true, timeout).subscribe(
            () => {
                const timeoutDate = new Date();
                timer(this._configuration.MaximumCheckInTimeout).subscribe(() => {
                    // only issue timeout if it is still being watched, and a new checkin hasn't started
                    if (this.checkinStatus[deviceId] && this.checkinStatus[deviceId] < timeoutDate) {
                        this.broadcastTimeout(deviceId);
                        this.stopCheckinInProgress(deviceId);
                    }
                });
            }, error => {
                delete this.checkinStatus[deviceId];
                this.broadcastError(deviceId, { deviceId, errorCode: 999, errorDescription: 'checkin failed' });
            });
        this.broadcastStatus();
    };

    private broadcastStatus = () => {
        this.onDeviceCheckinStatusChanged.postMessage({
            request: DeviceStatusRequest.Status,
            deviceCheckinStatus: this.checkinStatus
        });
    };

    private broadcastComplete = (data: DeviceCheckinCompleted) => {
        this.onDeviceCheckinStatusChanged.postMessage({
            request: DeviceStatusRequest.Completed,
            deviceCheckinStatus: this.checkinStatus,
            data
        });
    };

    private broadcastTimeout = (deviceId: string) => {
        this.onDeviceCheckinStatusChanged.postMessage({
            request: DeviceStatusRequest.Timeout,
            deviceCheckinStatus: this.checkinStatus,
            data: { deviceId, checkinTime: new Date(), result: DeviceCheckinResultCode.CheckinTimeout }
        });
    };

    private broadcastError = (deviceId: string, data: DeviceCheckinFailure) => {
        this.onDeviceCheckinStatusChanged.postMessage({
            deviceId,
            request: DeviceStatusRequest.Error,
            deviceCheckinStatus: this.checkinStatus,
            data
        });
    };

    private stopCheckinInProgress = (deviceId: string) => {
        delete this.checkinStatus[deviceId];
        this.broadcastStatus();
    };

    private setupHub(): Promise<signalR.HubConnection> {
        return this.getOneTimePasswordResource().then(stp =>
            // create hub connection
             new signalR.HubConnectionBuilder()
                .withUrl('/sotixsight/signalr/liveDataHub?stp=' + stp)
                .configureLogging(signalR.LogLevel.Information)
                .withAutomaticReconnect()
                .build()
        );
    }

    private startConnection(): void {
        this.connection.start()
            .then((data: any) => {
                console.log('SignalRCore connection established');
                this.globalEventBus.broadcast(GlobalEvents.signalrInitialized, true);
            }).catch((error: any) => {

                console.error('SignalRCore connection could not be established');
            });
    }

    private setToken(token: string) {
        this.connection.invoke('AccessToken', token);
    }

    private getOneTimePasswordResource = (): Promise<string> => {

        const deferred = new Promise<string>((resolve, reject) => {

            this.authenticationService.isAuthenticated().then(async () => {
                try {
                  const stp = await this.http.get<string>('signalr/stp').toPromise();
                  resolve(stp);
                } catch (error) {
                  reject(error);
                }
            }).catch(error => {
                reject(error);
            });
        });

        return deferred;
    };

    private ConfigureMessaging(): void {
        this.connection.on('onReauthenticateRequest', (data: string) => {
            this.reauthenticate();
        });

        this.connection.on('onDeviceCheckinCompleted', (data: DeviceCheckinCompleted) => {
            if (this.checkinStatus[data.deviceId]) {
                this.stopCheckinInProgress(data.deviceId);
                this.broadcastComplete(data);
            }
        });

        this.connection.on('onDeviceCheckinFailed', (data: DeviceCheckinFailure) => {
            if (this.checkinStatus[data.deviceId]) {
                this.stopCheckinInProgress(data.deviceId);
            }
        });

        this.connection.on('onTopicUpdate', (data: UntypedTopicUpdateModel) => {
            this.globalEventBus.broadcast(data.topicName, data.data);
        });
    }

    private reauthenticate() {
        this.authenticationService.isAuthenticated();
    }

}
